/**
 * PD-15480 — the scroll wheel must never enter a number.
 *
 * A focused `<input type="number">` steps its value on wheel, so scrolling a
 * long form with the cursor over a field silently edits it — and because the
 * wheel is consumed by the input, the page often does not scroll either, so
 * the user never sees what changed. Entering numbers by scrolling is not a
 * pattern we want anywhere in the product.
 *
 * This is installed once at the app root rather than opted into per field.
 * The rule is product-wide, there are well over a hundred numeric fields, and
 * a per-component guard silently stops applying the moment someone writes a
 * new `<input type="number">` — which is exactly how the fields that prompted
 * this bug were written.
 *
 * Mechanism: a number input only responds to the wheel while it is FOCUSED,
 * so blurring it on wheel removes the value change and leaves the page to
 * scroll normally. The listener is passive — it never calls preventDefault,
 * so scrolling itself is untouched.
 */

const isNumberInput = (node: Element | null): node is HTMLInputElement =>
	node != null &&
	node.tagName === 'INPUT' &&
	(node as HTMLInputElement).type === 'number';

/**
 * Install the guard. Returns a cleanup function that removes the listener,
 * so callers in a React effect can return it directly.
 */
export function installNumberInputWheelGuard(
	target: Document = document,
): () => void {
	const onWheel = (): void => {
		const active = target.activeElement;
		if (isNumberInput(active)) {
			active.blur();
		}
	};

	target.addEventListener('wheel', onWheel, { passive: true });

	return (): void => {
		target.removeEventListener('wheel', onWheel);
	};
}
